import React, { useCallback, useMemo } from 'react';

import { cssMap, cx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import { token } from '@atlaskit/tokens';
import { updatePageloadName } from '@atlaskit/react-ufo/update-pageload-name';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import UFOSegment from '@atlaskit/react-ufo/ufo-segment';

import { EnterpriseFeedbackComparisonPage } from '@atlassian/enterprise-feedback-comparison/enterprise-feedback-comparison';
import { SearchPageLite } from '@atlassian/search-page/ui';

import { expVal } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { TOUCH } from '@confluence/navdex/entry-points/NavdexPointType.ts';
import {
	createSingleQueryParamHook,
	useRouteActions,
} from '@confluence/route-manager/entry-points/RouteState.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { UserContext } from '@confluence/navigation-search-dialog/entry-points/UserContext.ts';
import { usePreloadDataProvider } from '@confluence/search-route-preloader/entry-points/usePreloadDataProvider.ts';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';
import { useGetNavigateToTeamsApp } from '@confluence/teams-app-config/entry-points/useGetNavigateToTeamsApp.ts';
import { AISmartButtonWrapper } from '@confluence/ai-rovo-button/entry-points/AISmartButtonWrapper.ts';

import { PageLoadEndEventUnifiedSearch } from './PageLoadEndEvent.tsx';

// TODO: Part of this list is supported filter query keys
// We can have those keys exporeted from search-page
// so when we add a new filter, we don't need to add it in two places
export const SEARCH_PAGE_QUERY_PARAM_KEYS = [
	'product',
	'text',
	'originalQuery', // This is used by "Did you mean" search correction feature
	'type',
	'lastModified',
	// "lastModified_lt",
	// "lastModified_gt",
	'titleMatch',
	'createdBy',
	'lastUpdatedBy',
	'contributors',
	'owners',
	'spaces',
	'sites',
	'pageStatus',
	'verified',
	'ancestor',
	'labels',
	'tags',
	'folders',
	'jAssignee',
	'jReporter',
	'jProject',
	'jCommentedByMe',
	'jWatchedByMe',
	'focusAreaTypes',
	'focusAreaUnder',
	'referrer',
	'leftOverrides',
	'rightOverrides',
	'from',
	'to',
	'cql',
	'archived',
	'filterDropdownOpen',
	'datasource',
	'status',
	'compassArchived',
	'assetsSchema',
	'assetsType',
	'salesforceStages',
	'campaignStatuses',
	'caseStatuses',
	'knowledgeArticleStatuses',
	'eventTypes',
	'leadStatuses',
	'taskStatuses',
	'taskTypes',
	'closeDate',
	'closeDateVal',
	'positionFocusAreas',
	'positionEmploymentTypes',
	'positionJobFamilies',
	'positionStatuses',
	'positionTeams',
	'isAutoApplied',
	'sort',
	'overrides',
	'opportunityStatus',
	'pullRequestStatus',
	'repository',
	'reviewer',
	'slackChannels',
	'workspaceName',
	'hasAttachment',
	'mode',
	'rovoJourneyId',
] as const;

const enableSideBySideQueryParams = ['leftOverrides', 'rightOverrides'];

const styles = cssMap({
	container: {
		marginLeft: token('space.negative.025'),
	},
	fillContainer: {
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
		minHeight: 0,
	},
	searchPageWithButtonWrapper: {
		position: 'relative',
		width: '100%',
	},
	fillSearchPage: {
		display: 'flex',
		flexDirection: 'column',
		flex: 1,
		minHeight: 0,
	},
	aiButtonPosition: {
		position: 'absolute',
		insetBlockEnd: token('space.300'),
		insetInlineEnd: token('space.300'),
	},
});

type QueryParamEntry = [string, string | undefined];

const createSearchQueryParamsHook = (queryParamKeys: readonly string[]) => {
	const queryParamHooks = queryParamKeys.map(
		(paramKey) =>
			[paramKey, createSingleQueryParamHook(paramKey)] as [
				string,
				ReturnType<typeof createSingleQueryParamHook>,
			],
	);

	return (): Record<string, any> => {
		const queryParamEntries = queryParamHooks
			.map(([paramKey, hook]) => [paramKey, hook()])
			.filter(([_, paramValue]) => !!paramValue);

		return Object.fromEntries(queryParamEntries);
	};
};

const getSearchQueryParams = createSearchQueryParamsHook(SEARCH_PAGE_QUERY_PARAM_KEYS);

const InnerSearchPage = ({ userEmail }: { userEmail?: string }) => {
	const {
		cloudId,
		userId,
		displayName,
		orgId,
		activationId,
		isAdminHubAIEnabled,
		isRovoLLMEnabled,
	} = useSessionData();
	const { push, setQueryParams } = useRouteActions();
	const setQueryParamsCallback = useCallback(
		(params, replace: boolean = false) => {
			const filteredQueryParams = SEARCH_PAGE_QUERY_PARAM_KEYS.map(
				(paramKey): QueryParamEntry => [paramKey, params[paramKey]],
			);

			setQueryParams(Object.fromEntries(filteredQueryParams), replace);
		},
		[setQueryParams],
	);

	interface UserDetails {
		id: string | null;
		name: string | null;
		email?: string | null;
	}

	const userDetails = useMemo<UserDetails>(() => {
		return {
			id: userId,
			name: displayName,
			email: userEmail,
		};
	}, [userId, displayName, userEmail]);

	const onNavigate = useCallback((url: string) => push(url), [push]);

	const queryParams = getSearchQueryParams();

	const getNavigateToTeamsApp = useGetNavigateToTeamsApp();
	const mapUserIdToProfileUrl = useCallback(
		(id: string) =>
			getNavigateToTeamsApp({
				type: 'USER',
				payload: {
					userId: id,
				},
			}).href,
		[getNavigateToTeamsApp],
	);

	const experimentLayers = StatsigConfigurations.getStringList(
		'search_platform_layer_functions',
		[],
	);
	const preloadDataProvider = usePreloadDataProvider();
	const searchPageProps: React.ComponentProps<typeof SearchPageLite> = useMemo(
		() => ({
			fillContainer: true,
			callbacks: { updateQueryParameters: setQueryParamsCallback },
			shell: {
				cloudId,
				orgId,
				user: userDetails,
				primaryProduct: 'confluence',
				analyticsAttributes: { activationId, navdexPointType: TOUCH },
				queryParams,
				isAdminHubAIEnabled,
				isNav4Enabled: true, //nav4 is rolled out, search platform can be cleaned up
				experimentLayers,
				isRovoLLMEnabled,
				preloadDataProvider,
			},
			mapUserIdToProfileUrl,
			onNavigate,
		}),
		[
			activationId,
			cloudId,
			experimentLayers,
			isAdminHubAIEnabled,
			isRovoLLMEnabled,
			mapUserIdToProfileUrl,
			onNavigate,
			orgId,
			preloadDataProvider,
			queryParams,
			setQueryParamsCallback,
			userDetails,
		],
	);

	return enableSideBySideQueryParams.some((key) => key in queryParams) &&
		expVal('enable-rovo_search_overrides', 'value', false) ? (
		<Box
			xcss={cx(styles.container, fg('rovo_fps_fix_duplicate_scrollbars') && styles.fillContainer)}
		>
			<EnterpriseFeedbackComparisonPage
				fillContainer
				callbacks={{ updateQueryParameters: setQueryParamsCallback }}
				shell={{
					cloudId,
					orgId,
					activationId,
					user: userDetails,
					primaryProduct: 'confluence',
					queryParams,
					isAdminHubAIEnabled,
					isNav4Enabled: true, //nav4 is rolled out, search platform can be cleaned up
					experimentLayers,
					isRovoLLMEnabled,
				}}
				mapUserIdToProfileUrl={mapUserIdToProfileUrl}
				onNavigate={onNavigate}
				testId="enterprise-feedback-comparison"
				entryPoint="confluence"
			/>
		</Box>
	) : (
		<Box
			xcss={cx(styles.container, fg('rovo_fps_fix_duplicate_scrollbars') && styles.fillContainer)}
		>
			{/* âš ï¸ if the props change on this component, please change EnterpriseFeedbackComparisonPage as well - it uses this component under the hood âš ï¸*/}
			<Box
				xcss={cx(
					styles.searchPageWithButtonWrapper,
					fg('rovo_fps_fix_duplicate_scrollbars') && styles.fillSearchPage,
				)}
			>
				<SearchPageLite {...searchPageProps} />
				<Box xcss={styles.aiButtonPosition}>
					<AISmartButtonWrapper
						contentId=""
						contentType=""
						pageMode="advanced-search"
						isDisabled={false}
					/>
				</Box>
			</Box>
			<PageLoadEndEventUnifiedSearch />
		</Box>
	);
};

export const ConfluenceSearchPage = (): React.JSX.Element => {
	// update advanced search page load name (used for UFO)
	useMemo(() => updatePageloadName('unified_search'), []);

	return (
		<UFOSegment name="advanced-search-search-page">
			<UserContext>
				{({ data, isLoading }) =>
					!isLoading ? (
						<InnerSearchPage userEmail={data?.email} />
					) : (
						<UFOLoadHold name="search-page-user-context-loading" />
					)
				}
			</UserContext>
		</UFOSegment>
	);
};
